import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  HStack,
  Input,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Textarea,
  TableContainer,
  chakra,
  useColorModeValue,
  Spinner,
  Center,
  Text,
  IconButton,
  Icon,
} from "@chakra-ui/react";
import { AddIcon, DownloadIcon, AttachmentIcon } from "@chakra-ui/icons";
import { fetchCardAttachments, uploadCardAttachment } from "../../../../ProjectBoardServices/BoardModalAPI/LeftModalAPI/AttachmentsApi";

/* ---------------- HEADER COMPONENT ---------------- */
const TabHeaderBar = ({
  visible,
  onToggle,
  file,
  onFileChange,
  notes,
  onNotesChange,
  uploading,
}) => {
  const fileBoxBg = useColorModeValue("gray.50", "gray.700");
  const borderCol = useColorModeValue("gray.300", "gray.600");
  const cardBg = useColorModeValue("white", "gray.800");
  const hoverShadow = useColorModeValue("md", "lg");
  const fileUploadHoverBg = useColorModeValue("blue.50", "gray.600");

  // Improved floating label logic
  const isNotesFloating = notes.length > 0;

  return (
    <Box
      mb={4}
      p={4}
      bg={cardBg}
      border="1px solid"
      borderColor={borderCol}
      borderRadius="lg"
      shadow="sm"
      transition="all 0.2s ease"
      _hover={{ shadow: hoverShadow }}
    >
      <Flex align="center" justify="space-between" flexWrap="wrap" gap={3}>
        <HStack spacing={4} flexWrap="wrap">
          {visible && (
            <>
              {/* File Upload */}
              <Box
                position="relative"
                border="2px dashed"
                borderColor={borderCol}
                borderRadius="lg"
                p={3}
                w="180px"
                textAlign="center"
                bg={fileBoxBg}
                cursor="pointer"
                transition="all 0.25s ease"
                _hover={{
                  borderColor: "blue.400",
                  bg: fileUploadHoverBg,
                }}
              >
                <Icon
                  as={AttachmentIcon}
                  boxSize={4}
                  color={file ? "blue.500" : "gray.400"}
                  mb={1}
                />
                <Text
                  fontSize="xs"
                  color={file ? "blue.600" : "gray.600"}
                  fontWeight="medium"
                  noOfLines={1}
                >
                  {file ? file.name : "Click to upload"}
                </Text>
                <Input
                  type="file"
                  position="absolute"
                  top={0}
                  left={0}
                  w="100%"
                  h="100%"
                  opacity={0}
                  cursor="pointer"
                  onChange={onFileChange}
                />
              </Box>

              {/* Fixed Floating Label Textarea */}
              <Box position="relative" w="230px">
                <Textarea
                  value={notes}
                  onChange={onNotesChange}
                  placeholder=" "
                  size="sm"
                  fontSize="sm"
                  resize="none"
                  border="1px solid"
                  borderColor={borderCol}
                  borderRadius="lg"
                  bg={cardBg}
                  h="80px"
                  pt="6"                    // Extra top padding for floating label
                  _focus={{
                    borderColor: "blue.400",
                    boxShadow: "0 0 0 1px #3182ce",
                  }}
                  _hover={{ borderColor: "gray.400" }}
                  transition="all 0.2s ease"
                />

                {/* Floating Label */}
                <Text
                  position="absolute"
                  top={isNotesFloating ? "8px" : "50%"}
                  left="12px"
                  transform={isNotesFloating ? "none" : "translateY(-50%)"}
                  fontSize={isNotesFloating ? "xs" : "sm"}
                  color={isNotesFloating ? "blue.500" : "gray.500"}
                  bg={cardBg}
                  px={2}
                  pointerEvents="none"
                  transition="all 0.2s ease"
                  zIndex={1}
                >
                  Add notes...
                </Text>
              </Box>
            </>
          )}
        </HStack>

        {/* Action Button */}
        <Button
          size="sm"
          leftIcon={<AddIcon />}
          colorScheme="blue"
          borderRadius="full"
          onClick={onToggle}
          fontWeight="semibold"
          isLoading={uploading}
          loadingText="Uploading"
          isDisabled={visible && !file}
          _hover={{ transform: "translateY(-1px)", shadow: "md" }}
          transition="all 0.2s ease"
        >
          {visible ? "Submit" : "Add Attachment"}
        </Button>
      </Flex>
    </Box>
  );
};

/* ---------------- MAIN COMPONENT ---------------- */
const AttachmentsTab = ({ task }) => {
  const [showHeaderActions, setShowHeaderActions] = useState(false);
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const limit = 10;

  const headerBg = useColorModeValue("blue.500", "blue.600");
  const headerColor = useColorModeValue("white", "gray.100");
  const rowHoverBg = useColorModeValue("blue.50", "gray.700");
  const tableBg = useColorModeValue("white", "gray.800");
  const borderCol = useColorModeValue("gray.200", "gray.600");

  const fetchAttachments = async (pageNum = 1) => {
    setLoading(true);
    try {
      const res = await fetchCardAttachments(task?.id, limit, pageNum);
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setAttachments(data);
      setTotal(res.data?.total || data.length);
      setPage(pageNum);
    } catch (err) {
      console.error("Error fetching attachments:", err);
      setAttachments([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttachments();
  }, [task?.id]); // Added task.id dependency

  const handleFileChange = (e) => setFile(e.target.files[0] || null);
  const handleNotesChange = (e) => setNotes(e.target.value);

  const handleToggle = async () => {
    if (showHeaderActions) {
      if (!file) return;

      setUploading(true);
      const formData = new FormData();
      formData.append("media", file);
      formData.append("notes", notes);

      try {
        await uploadCardAttachment(task?.id, formData);
        setFile(null);
        setNotes("");
        setShowHeaderActions(false);
        await fetchAttachments(page); // Refresh current page
      } catch (err) {
        console.error("Failed to upload attachment:", err);
        alert("Failed to upload attachment");
      } finally {
        setUploading(false);
      }
    } else {
      setShowHeaderActions(true);
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <Box p={4} shadow="md" bg={tableBg} borderRadius="md">
      {/* Header Bar */}
      <TabHeaderBar
        visible={showHeaderActions}
        onToggle={handleToggle}
        file={file}
        onFileChange={handleFileChange}
        notes={notes}
        onNotesChange={handleNotesChange}
        uploading={uploading}
      />

      {/* Scrollable Table Container */}
      <TableContainer
        border="1px solid"
        borderColor={borderCol}
        borderRadius="md"
        maxH="400px"
        overflowY="auto"
        css={{
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
          "&::-webkit-scrollbar-thumb": { background: "#888", borderRadius: "4px" },
          "&::-webkit-scrollbar-thumb:hover": { background: "#555" },
        }}
      >
        <Table variant="simple" size="sm">
          <Thead bg={headerBg} position="sticky" top={0} zIndex={10}>
            <Tr>
              <Th color={headerColor} fontSize="xs" py={3}>
                Name
              </Th>
              <Th color={headerColor} fontSize="xs" py={3}>
                Uploaded By
              </Th>
              <Th color={headerColor} fontSize="xs" py={3}>
                Notes
              </Th>
              <Th color={headerColor} fontSize="xs" py={3}>
                Action
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={4}>
                  <Center py={8}>
                    <Spinner size="sm" color="blue.500" />
                  </Center>
                </Td>
              </Tr>
            ) : attachments.length === 0 ? (
              <Tr>
                <Td colSpan={4}>
                  <Center py={8}>
                    <Text fontSize="xs" color="gray.500">
                      No attachments found
                    </Text>
                  </Center>
                </Td>
              </Tr>
            ) : (
              attachments.map((row) => (
                <Tr
                  key={row.id}
                  _hover={{
                    bg: rowHoverBg,
                    transition: "all 0.2s",
                  }}
                >
                  <Td fontSize="xs">{row.originalName || "-"}</Td>
                  <Td fontSize="xs">{row.uploadedBy?.name || "-"}</Td>
                  <Td fontSize="xs" whiteSpace="pre-wrap" maxW="150px">
                    <chakra.span color="gray.600">
                      {row.notes?.trim() || "-"}
                    </chakra.span>
                  </Td>
                  <Td fontSize="xs">
                    {row.url ? (
                      <IconButton
                        as="a"
                        href={row.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="xs"
                        colorScheme="blue"
                        icon={<DownloadIcon />}
                        aria-label="Download"
                      />
                    ) : (
                      "-"
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Flex justify="center" align="center" mt={3}>
          <HStack spacing={2}>
            <Button
              size="xs"
              onClick={() => fetchAttachments(page - 1)}
              isDisabled={page === 1}
            >
              Prev
            </Button>
            <Text fontSize="xs">
              Page {page} of {totalPages}
            </Text>
            <Button
              size="xs"
              onClick={() => fetchAttachments(page + 1)}
              isDisabled={page === totalPages}
            >
              Next
            </Button>
          </HStack>
        </Flex>
      )}
    </Box>
  );
};

export default AttachmentsTab;